import { Box, Heading, Image, Text } from '@chakra-ui/react';
import domtoimage from 'dom-to-image';
import converter from 'number-to-words';
import { useState } from 'react';

import Logo from '../../../assets/images/9jaPay Logo_Horizontal.svg';
import ReceiptQRCode from '../../../assets/images/receipt-qr.svg';
import {
  formatDate,
  formatDateAndTime,
  formatNumber,
  getFormattedTime,
} from '../../../helpers/utilityFunctions';
import { useAppDispatch } from '../../../store/hooks';
import { showAlert } from '../../../store/slices/appToast/appToastSlice';
import { TransactionType } from '../../../types/transactionType';
import { ArrowDownTray } from '../../icons';
import { PillButton } from '../CustomButtons';
import ReceiptData from './ReceiptData';
import ReceiptHeader from './ReceiptHeader';
import ReceiptRow from './ReceiptRow';
import ReceiptTitle from './ReceiptTitle';

interface TransactionReceiptProps {
  date: string;
  amount: number;
  transactionType: TransactionType;
  transactionReference: string;
  isBillPaymentType?: boolean;
  address?: string;
  transactionFee?: number | string;
  beneficiaryName?: string;
  destinationBank?: string;
  accountNumber?: number | string;
  senderName: string;
  senderBank?: string;
  senderAccountNumber?: number | string;
  narration?: string;
  provider?: string;
  device?: string;
  transferNote?: string;
  showAmountInWords?: boolean;
  transactionStatus?: string;
}

export default function TransactionReceipt({
  accountNumber,
  amount,
  beneficiaryName,
  date,
  destinationBank,
  senderName,
  senderBank,
  transactionReference,
  transactionType,
  transferNote,
  transactionFee,
  senderAccountNumber,
  narration,
  provider,
  device,
  address,
  isBillPaymentType,
  transactionStatus,
  showAmountInWords = true,
}: TransactionReceiptProps) {
  const dispatch = useAppDispatch();

  const [generatingReceipt, setGeneratingReceipt] = useState(false);

  const handleShareClick = () => {
    const nodeToPrint = document.getElementById('transaction-receipt');

    domtoimage
      .toJpeg(nodeToPrint as Node, {
        quality: 1,
      })
      .then(function (dataUrl) {
        setGeneratingReceipt(true);

        const link = document.createElement('a');
        link.download = `9jaPay-Receipt-${date}.jpeg`;
        link.href = dataUrl;
        link?.click();

        setGeneratingReceipt(false);
        dispatch(
          showAlert({
            status: 'success',
            description: 'Receipt Downloaded',
            type: 'drawer',
          }),
        );
      })
      .catch(function (error) {
        setGeneratingReceipt(false);
        dispatch(
          showAlert({
            status: 'error',
            description: 'Failed to download receipt, try again',
          }),
        );
      });
  };

  return (
    <Box position="relative">
      {transactionStatus !== 'pending_tsq' && (
        <PillButton
          fontSize=".875rem"
          maxHeight="27px"
          sx={{ borderRadius: '8px', background: 'green.500' }}
          onClick={handleShareClick}
          isLoading={generatingReceipt}
          position="absolute"
          right="0"
          top="20px"
        >
          Share <ArrowDownTray ml={1} boxSize="14px" />
        </PillButton>
      )}

      <Box mx={-4}>
        <Box id="transaction-receipt" background="#fff">
          <Box display="block" mx="auto" px={4} py={4}>
            <Box
              mb="44px"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Image
                src={Logo}
                alt="Logo"
                maxWidth="108px"
                width="100%"
                height="auto"
              />
            </Box>

            <section style={{ marginBottom: '44px' }}>
              <Box display="flex" justifyContent="end" mb="12px">
                <Box
                  padding="4px 8px"
                  borderRadius="32px"
                  background={
                    transactionStatus?.toLowerCase() === 'failed'
                      ? '#FAE8EB'
                      : transactionStatus?.toLowerCase() === 'pending' ||
                          transactionStatus?.toLowerCase() === 'pending_tsq'
                        ? '#FAF7E8'
                        : '#e8faee'
                  }
                >
                  <Text
                    color={
                      transactionStatus?.toLowerCase() === 'failed'
                        ? '#CC1433'
                        : transactionStatus?.toLowerCase() === 'pending' ||
                            transactionStatus?.toLowerCase() === 'pending_tsq'
                          ? '#CCAD14'
                          : '#0e913a'
                    }
                    fontSize="10px"
                    lineHeight="155.556%"
                    textTransform="uppercase"
                    whiteSpace="nowrap"
                  >
                    {transactionStatus}
                  </Text>
                </Box>
              </Box>

              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                gap="50px"
              >
                <Box>
                  <Heading
                    as="h1"
                    fontWeight={500}
                    fontSize="1rem"
                    lineHeight="127.23%"
                    whiteSpace="nowrap"
                    mb="4px"
                  >
                    Transaction Receipt
                  </Heading>
                  <Text
                    fontSize=".875rem"
                    lineHeight="114.286%"
                    color="#656B76"
                  >
                    {formatDate(date)}, {getFormattedTime(date)}
                  </Text>
                </Box>
                <Box>
                  <Text
                    display="inline-flex"
                    alignItems="baseline"
                    gap="2px"
                    fontSize="1.125rem"
                    lineHeight="127.23%"
                    fontWeight={500}
                  >
                    <span>₦</span>
                    <span
                      style={{ fontSize: '1.5rem', lineHeight: '111.111%' }}
                    >
                      {formatNumber(amount).nairaValue}
                    </span>
                    <span>.{formatNumber(amount).koboValue}</span>
                  </Text>
                </Box>
              </Box>
            </section>

            <section style={{ marginBottom: '44px' }}>
              <Box
                background="#F8F5FF"
                display="flex"
                flexDirection="column"
                gap={{ base: '38px', lg: '44px' }}
                padding="44px 30px"
                borderRadius="24px"
              >
                <section>
                  <ReceiptHeader>Transaction Details</ReceiptHeader>

                  <Box
                    display="flex"
                    flexDirection="column"
                    gap={{ base: '24px', lg: '16px' }}
                  >
                    <ReceiptRow>
                      <ReceiptTitle>Transaction Type</ReceiptTitle>
                      <ReceiptData>{transactionType}</ReceiptData>
                    </ReceiptRow>

                    <ReceiptRow>
                      <ReceiptTitle>Transaction Reference</ReceiptTitle>
                      <ReceiptData>
                        {transactionReference ?? '----'}
                      </ReceiptData>
                    </ReceiptRow>

                    {address && (
                      <ReceiptRow>
                        <ReceiptTitle>Address</ReceiptTitle>
                        <ReceiptData>{address ?? '----'}</ReceiptData>
                      </ReceiptRow>
                    )}

                    {!!transactionFee && (
                      <ReceiptRow>
                        <ReceiptTitle>Transaction Fee</ReceiptTitle>
                        <ReceiptData>{transactionFee}</ReceiptData>
                      </ReceiptRow>
                    )}

                    {showAmountInWords && (
                      <ReceiptRow>
                        <ReceiptTitle>Amount in Words</ReceiptTitle>
                        <ReceiptData textTransform="capitalize">
                          {converter.toWords(Number.isNaN(amount) ? 0 : amount)}{' '}
                          Naira
                        </ReceiptData>
                      </ReceiptRow>
                    )}
                  </Box>
                </section>

                {(beneficiaryName || destinationBank || accountNumber) &&
                  !isBillPaymentType && (
                    <section>
                      <ReceiptHeader>Beneficiary Details</ReceiptHeader>

                      <Box display="flex" flexDirection="column" gap="16px">
                        <ReceiptRow>
                          <ReceiptTitle>Account Name</ReceiptTitle>
                          <ReceiptData>{beneficiaryName}</ReceiptData>
                        </ReceiptRow>

                        <ReceiptRow>
                          <ReceiptTitle>Destination Bank</ReceiptTitle>
                          <ReceiptData>{destinationBank}</ReceiptData>
                        </ReceiptRow>

                        <ReceiptRow>
                          <ReceiptTitle>Account Number</ReceiptTitle>
                          <ReceiptData>{accountNumber}</ReceiptData>
                        </ReceiptRow>
                      </Box>
                    </section>
                  )}

                {(senderName || senderBank || transferNote) && (
                  <section>
                    <ReceiptHeader>Sender Details</ReceiptHeader>

                    <Box display="flex" flexDirection="column" gap="16px">
                      <ReceiptRow>
                        <ReceiptTitle>Account Name</ReceiptTitle>
                        <ReceiptData>{senderName}</ReceiptData>
                      </ReceiptRow>

                      <ReceiptRow>
                        <ReceiptTitle>Sender Bank</ReceiptTitle>
                        <ReceiptData>
                          {senderBank ?? '9jaPay Microfinance Bank'}
                        </ReceiptData>
                      </ReceiptRow>

                      {transferNote && (
                        <ReceiptRow>
                          <ReceiptTitle>Transfer Note</ReceiptTitle>
                          <ReceiptData>{transferNote}</ReceiptData>
                        </ReceiptRow>
                      )}

                      {senderAccountNumber && (
                        <ReceiptRow>
                          <ReceiptTitle>Account Number</ReceiptTitle>
                          <ReceiptData>{senderAccountNumber}</ReceiptData>
                        </ReceiptRow>
                      )}
                    </Box>
                  </section>
                )}

                {(narration || provider || device) && (
                  <section>
                    <ReceiptHeader>More Information</ReceiptHeader>

                    <Box display="flex" flexDirection="column" gap="16px">
                      <ReceiptRow>
                        <ReceiptTitle>Narration</ReceiptTitle>
                        <ReceiptData>
                          {narration
                            ? `${transactionType} - ${formatDateAndTime(date)}`
                            : '--'}
                        </ReceiptData>
                      </ReceiptRow>

                      <ReceiptRow>
                        <ReceiptTitle>Provider</ReceiptTitle>
                        <ReceiptData>{provider || '--'}</ReceiptData>
                      </ReceiptRow>

                      <ReceiptRow>
                        <ReceiptTitle>Biller Number</ReceiptTitle>
                        <ReceiptData>{device || '--'}</ReceiptData>
                      </ReceiptRow>
                    </Box>
                  </section>
                )}
              </Box>
            </section>

            <section>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                gap="20px"
              >
                <Box>
                  <Text
                    fontSize={{ base: '1rem', lg: '1.125rem' }}
                    fontWeight={700}
                    lineHeight="120%"
                    mb="8px"
                  >
                    9jaPay - Making More{' '}
                    <Box
                      as="span"
                      fontStyle="italic"
                      position="relative"
                      fontFamily="RecklessNeue"
                    >
                      <span style={{ position: 'relative', zIndex: 1 }}>
                        Possible!
                      </span>
                      <Box
                        as="svg"
                        position="absolute"
                        zIndex={0}
                        bottom="-20px"
                        left="-2px"
                        width={{ base: '72px', lg: '82px' }}
                        height="12"
                        viewBox="0 0 223 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect
                          width="223"
                          height="11"
                          transform="translate(0 0.5)"
                          fill="#14CC52"
                        />
                      </Box>
                    </Box>
                  </Text>

                  <Text fontSize=".75rem" lineHeight="114.286%">
                    Available on the Apple AppStore and the Google PlayStore
                  </Text>
                </Box>

                <Image
                  src={ReceiptQRCode}
                  alt="QR Code"
                  width="60px"
                  maxWidth="100%"
                  height="auto"
                />
              </Box>
            </section>

            <section style={{ marginTop: '32px' }}>
              <Text
                fontSize=".75rem"
                lineHeight="150%"
                color="#656B76"
                mb="8px"
              >
                © {new Date().getFullYear()} 9jaPay Microfinance Bank. All
                rights reserved.
              </Text>
              <Text
                fontSize=".75rem"
                lineHeight="150%"
                color="#656B76"
                mb="8px"
              >
                Banking services offered on this platform are provided by
                9japay Microfinance Bank Limited (RC 787934) with its registered
                address at 270, Murtala Muhammed Way, Alagomeji-Yaba, Lagos.
                9japay Microfinance Bank Limited is licensed by the Central Bank
                of Nigeria.
              </Text>
              <Text fontSize=".75rem" lineHeight="150%" color="#656B76">
                Other services offered on this platform are provided by the
                respective named service providers who have represented to AFL
                to be duly licensed/legally permitted to provide those services.
              </Text>
            </section>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
